import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ListItemText } from 'components/ui';
import {
  ArrowDropDownIcon,
  ArrowDropUpIcon,
  ProbelistProfileIcon,
  ProbelistLogoutIcon,
  ProbelistSettingsIcon,
} from 'components/uiIcons';
import { useMutation } from '@apollo/client/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProfileLink } from 'utils/link';
import { ListMenuItem, MenuButton } from 'components/styles_components/menu';
import { removeAuthenticated } from 'utils/authentication';
import { useThemeMode } from 'contexts/theme';
import { useLocale } from 'contexts/locale';
import { LimitedSpan } from 'components/styles_components/span';
import LogoutMutation from 'mutations/user/logout';
import useUserHeader from 'hooks/use_user_header';
import { userLabel } from 'utils/user';

const ProfileButton = ({ isMobile }) => {
  const intl = useIntl();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileOpen = Boolean(profileAnchorEl);
  const navigate = useNavigate();
  const [logout, logoutMutationData] = useMutation(LogoutMutation, {
    onCompleted: () => {
      removeAuthenticated();
    },
  });
  const { user, loading } = useUserHeader();
  const { mode, setMode } = useThemeMode();
  const { locale, setLocale } = useLocale();

  useEffect(() => {
    if (user?.darkMode === undefined) return;
    const dbMode = user.darkMode ? 'dark' : 'light';
    if (dbMode !== mode) {
      setMode(dbMode);
    }
  }, [user?.darkMode, mode, setMode]);

  useEffect(() => {
    if (!user?.locale) return;
    if (user.locale !== locale) {
      setLocale(user.locale);
    }
  }, [user?.locale, locale, setLocale]);

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const onItemClick = (link) => {
    handleProfileClose();
    navigate(link);
  };

  const userName = userLabel(user, '');
  const icon = profileOpen ? <ArrowDropUpIcon aria-hidden="true" /> : <ArrowDropDownIcon aria-hidden="true" />;

  return (
    <>
      <MenuButton
        loading={loading}
        aria-label={intl.formatMessage({ id: 'aria.profile.open' })}
        aria-haspopup="menu"
        aria-expanded={profileOpen}
        aria-controls={profileOpen ? 'profile-menu' : undefined}
        onClick={handleProfileClick}
      >
        <ProbelistProfileIcon sx={isMobile ? {} : { mr: 1 }} aria-hidden="true" />
        {!isMobile && <LimitedSpan>{userName}</LimitedSpan>}
        {icon}
      </MenuButton>
      <Menu id="profile-menu" anchorEl={profileAnchorEl} open={profileOpen} onClose={handleProfileClose}>
        <ListMenuItem onClick={() => onItemClick(ProfileLink())}>
          <ProbelistSettingsIcon aria-hidden="true" />
          <ListItemText>
            <FormattedMessage id="navbar.profile" />
          </ListItemText>
        </ListMenuItem>
        <ListMenuItem onClick={logout} disabled={logoutMutationData.loading}>
          <ProbelistLogoutIcon aria-hidden="true" />
          <ListItemText>
            <FormattedMessage id="navbar.logout" />
          </ListItemText>
        </ListMenuItem>
      </Menu>
    </>
  );
};

export default ProfileButton;
